if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        if (this?.variantIdInput) {
          this.variantIdInput.disabled = false;
        }
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";

        /**
         * Retry configuration for add-to-cart operations with exponential backoff
         *
         * How it works:
         * - Automatically retries failed add-to-cart requests
         * - Wait time doubles after each failed attempt (exponential backoff)
         * - Example with current settings (initialWaitTime: 2000ms, maxAttempts: 4):
         *   1. First attempt fails → Wait 2 seconds → Retry
         *   2. Second attempt fails → Wait 4 seconds → Retry
         *   3. Third attempt fails → Wait 8 seconds → Retry
         *   4. Fourth attempt fails → Give up and show error
         *
         * Configuration:
         * - initialWaitTime: Starting delay in milliseconds (default: 2000 = 2 seconds)
         * - maxAttempts: Maximum number of attempts including the first try (default: 4)
         *
         * Formula: waitTime = initialWaitTime × 2^(attempt - 1)
         */
        this.retryConfig = {
          initialWaitTime: 2000, // Start with 2 seconds
          maxAttempts: 4, // Maximum number of retry attempts
        };
      }

      /**
       * Retry function with exponential backoff
       *
       * Implements an exponential backoff strategy for retrying failed operations.
       * The wait time doubles with each retry attempt, reducing server load while
       * giving temporary issues time to resolve.
       *
       * @param {Function} fetchFn - Function that returns a fetch promise
       * @param {Number} attempt - Current attempt number (default: 1)
       * @returns {Promise} - Resolves with response or rejects after max attempts
       *
       * @example
       * // With default config (initialWaitTime: 2000ms, maxAttempts: 4):
       * // Attempt 1 fails → Wait 2s  → Attempt 2
       * // Attempt 2 fails → Wait 4s  → Attempt 3
       * // Attempt 3 fails → Wait 8s  → Attempt 4
       * // Attempt 4 fails → Throw error
       */
      async retryWithBackoff(fetchFn, attempt = 1) {
        try {
          const response = await fetchFn();
          return response;
        } catch (error) {
          // If we've exhausted all retry attempts, throw the error
          if (attempt >= this.retryConfig.maxAttempts) {
            throw error;
          }

          // Calculate wait time with exponential backoff
          // Formula: waitTime = initialWaitTime × 2^(attempt - 1)
          const waitTime = this.retryConfig.initialWaitTime * Math.pow(2, attempt - 1);

          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, waitTime));

          // Retry with incremented attempt number
          return this.retryWithBackoff(fetchFn, attempt + 1);
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        // console.log(this);
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // Wrap fetch in retry function with exponential backoff
        // This will automatically retry failed requests with increasing wait times
        const fetchWithRetry = () =>
          fetch(`${routes.cart_add_url}`, config)
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }
              return response.json();
            });

        this.retryWithBackoff(fetchWithRetry)
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add("hidden");
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}
